import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';
import CampaignForm from './CampaignForm';

const BASE_URL = '/admin/campaigns';

function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function AdminCampaigns() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState(null);

  const editing = id === undefined ? null
    : id === 'new' ? {}
    : campaigns.find(c => String(c.id) === id) ?? null;

  // Bad/stale id (e.g. deleted item, typo'd link) -> back to the list,
  // but only once loading has actually finished.
  useEffect(() => {
    if (!loading && id && id !== 'new' && !editing) navigate(BASE_URL, { replace: true });
  }, [loading, id, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => {
    setLoading(true);
    api.get(`${QUALITY_SERVICE_API_URL}/hub/campaigns`)
      .then(r => setCampaigns(r.data))
      .catch(() => setError('Failed to load campaigns.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleSave = async (data) => {
    setSaving(true);
    setError(null);
    try {
      if (editing.id) {
        await api.put(`${QUALITY_SERVICE_API_URL}/hub/campaigns/${editing.id}`, data);
      } else {
        await api.post(`${QUALITY_SERVICE_API_URL}/hub/campaigns`, data);
      }
      navigate(BASE_URL);
      load();
    } catch {
      setError('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (campaign) => {
    if (!window.confirm(`Delete "${campaign.campaignName}"? This cannot be undone.`)) return;
    try {
      await api.delete(`${QUALITY_SERVICE_API_URL}/hub/campaigns/${campaign.id}`);
      load();
    } catch {
      setError('Delete failed.');
    }
  };

  return (
    <div className="admin-campaigns">
      <div className="admin-section-toolbar">
        <h2 className="admin-section-title">Campaigns</h2>
        <button className="admin-btn-primary" onClick={() => navigate(`${BASE_URL}/new`)}>+ New Campaign</button>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Label</th>
              <th>Start</th>
              <th>End</th>
              <th>Leads</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map(c => (
              <tr key={c.id}>
                <td className="admin-td-name">{c.campaignName}</td>
                <td><code className="admin-label-chip">{c.campaignLabel}</code></td>
                <td>{fmt(c.startDate)}</td>
                <td>{fmt(c.endDate)}</td>
                <td className="admin-td-leads">{c.leads || '—'}</td>
                <td className="admin-td-actions">
                  <button className="admin-btn-edit"   onClick={() => navigate(`${BASE_URL}/${c.id}`)}>Edit</button>
                  <button className="admin-btn-delete" onClick={() => handleDelete(c)}>Delete</button>
                </td>
              </tr>
            ))}
            {campaigns.length === 0 && (
              <tr><td colSpan={6} className="admin-empty">No campaigns yet.</td></tr>
            )}
          </tbody>
        </table>
      )}

      {editing !== null && (
        <CampaignForm
          campaign={editing}
          onSave={handleSave}
          onCancel={() => { navigate(BASE_URL); setError(null); }}
          saving={saving}
        />
      )}
    </div>
  );
}

export default AdminCampaigns;
